"use client";

import { useCallback, useMemo } from "react";
import {
  SourceCitationBuilder,
  type SourceField,
  type SourceValues,
} from "@/components/tool-kit/source-citation-builder";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  type ClassicalWorkLabels,
  classicalWorkIsReady,
  CLASSICAL_WORK_FIELDS as F,
  formatClassicalWork,
} from "@/lib/tool-kit/special-sources/classical-work-citation-generator";

/** English strings. A translated content file overrides these through `ui`. */
export const CLASSICAL_WORK_CITATION_GENERATOR_STRINGS = {
  formHeading: "Classical work details",
  resultsHeading: "Your citations",
  emptyPrompt: "Enter the title of the work to see your citations.",
  copy: "Copy",
  copied: "Copied!",
  tryExample: "Try an example",
  clear: "Clear",
  authorLabel: "Author",
  authorPlaceholder: "Aristotle",
  authorHint:
    "The name the author goes by. Leave it blank for an anonymous work such as The Epic of Gilgamesh.",
  titleLabel: "Title, as your edition gives it",
  titlePlaceholder: "Poetics",
  titleHint:
    "Type it in sentence case — only the first word and proper nouns capitalized. The tool capitalizes it for MLA and Chicago.",
  shortTitleLabel: "Short title for citations (optional)",
  shortTitlePlaceholder: "Odyssey",
  locationLabel: "Passage, by its standard number",
  locationPlaceholder: "1450b26–27",
  locationHint:
    "Book and line for epic (1.1–5), Stephanus numbers for Plato (514a), Bekker numbers for Aristotle (1450b26) — the numbers printed in the margins of most editions.",
  translatorsLabel: "Translators, one per line",
  translatorsPlaceholder: "Malcolm Heath",
  editorsLabel: "Editors, one per line (optional)",
  publisherLabel: "Publisher of your edition",
  publisherPlaceholder: "Penguin Books",
  yearLabel: "Year of your edition",
  yearPlaceholder: "1996",
  urlLabel: "URL, for an online edition (optional)",
  urlPlaceholder: "https://",
  originalYearLabel: "Original date (optional)",
  originalYearPlaceholder: "335",
  originalYearHint:
    "Leave it blank when the date is unknown or disputed, as it is for Homer. A range such as 2750–2500 is fine.",
  eraLabel: "Era of the original date",
  eraBce: "B.C.E. (before the common era)",
  eraCe: "C.E. (common era)",
  eraNone: "No era — a later date",
  circaLabel: "The original date is approximate (ca.)",
  apaLabel: "APA 7th edition",
  mlaLabel: "MLA 9th edition",
  chicagoLabel: "Chicago 18th edition (notes and bibliography)",
  referenceList: "Reference list",
  parenthetical: "In-text citation (parenthetical)",
  narrative: "In-text citation (narrative)",
  worksCited: "Works Cited",
  inText: "In-text citation",
  footnote: "Footnote",
  bibliography: "Bibliography",
  apaNote:
    "APA cites the edition you used. Give the original date only when it is known — with ca. for an approximate one — and locate quotations by the standard numbers, not page numbers.",
  mlaNote:
    "Cite the translation you read. In the text, the standard numbers replace page numbers, divided by periods: book 1, lines 1–5 is 1.1–5.",
  chicagoNote:
    "Chicago cites classical works in notes by their standard numbers. With no passage entered, the note cites the edition instead. List the edition in your bibliography when you quote its translation or use its notes.",
  example: {
    author: "Aristotle",
    title: "Poetics",
    translators: "Malcolm Heath",
    publisher: "Penguin Books",
    year: "1996",
    originalYear: "335",
    era: "bce",
    circa: "true",
    location: "1450b26–27",
  },
} as const;

type Strings = typeof CLASSICAL_WORK_CITATION_GENERATOR_STRINGS;

function buildFields(strings: Strings): SourceField[] {
  return [
    {
      id: F.author,
      kind: "text",
      label: strings.authorLabel,
      placeholder: strings.authorPlaceholder,
      hint: strings.authorHint,
    },
    {
      id: F.location,
      kind: "text",
      label: strings.locationLabel,
      placeholder: strings.locationPlaceholder,
      hint: strings.locationHint,
    },
    {
      id: F.title,
      kind: "text",
      label: strings.titleLabel,
      placeholder: strings.titlePlaceholder,
      hint: strings.titleHint,
    },
    {
      id: F.shortTitle,
      kind: "text",
      label: strings.shortTitleLabel,
      placeholder: strings.shortTitlePlaceholder,
    },
    {
      id: F.translators,
      kind: "people",
      label: strings.translatorsLabel,
      placeholder: strings.translatorsPlaceholder,
    },
    {
      id: F.editors,
      kind: "people",
      label: strings.editorsLabel,
    },
    {
      id: F.publisher,
      kind: "text",
      label: strings.publisherLabel,
      placeholder: strings.publisherPlaceholder,
    },
    {
      id: F.year,
      kind: "text",
      label: strings.yearLabel,
      placeholder: strings.yearPlaceholder,
    },
    {
      id: F.originalYear,
      kind: "text",
      label: strings.originalYearLabel,
      placeholder: strings.originalYearPlaceholder,
      hint: strings.originalYearHint,
    },
    {
      id: F.era,
      kind: "select",
      label: strings.eraLabel,
      options: [
        { value: "bce", label: strings.eraBce },
        { value: "ce", label: strings.eraCe },
        { value: "none", label: strings.eraNone },
      ],
    },
    {
      id: F.circa,
      kind: "checkbox",
      label: strings.circaLabel,
    },
    {
      id: F.url,
      kind: "url",
      label: strings.urlLabel,
      placeholder: strings.urlPlaceholder,
    },
  ];
}

/** The widget's strings, as the formatter's labels. */
export function classicalWorkLabels(strings: Strings): ClassicalWorkLabels {
  return {
    apa: strings.apaLabel,
    mla: strings.mlaLabel,
    chicago: strings.chicagoLabel,
    referenceList: strings.referenceList,
    parenthetical: strings.parenthetical,
    narrative: strings.narrative,
    worksCited: strings.worksCited,
    inText: strings.inText,
    footnote: strings.footnote,
    bibliography: strings.bibliography,
    apaNote: strings.apaNote,
    mlaNote: strings.mlaNote,
    chicagoNote: strings.chicagoNote,
  };
}

export default function ClassicalWorkCitationGeneratorWidget() {
  const strings = useToolUi(CLASSICAL_WORK_CITATION_GENERATOR_STRINGS);
  const fields = useMemo(() => buildFields(strings), [strings]);
  const labels = useMemo(() => classicalWorkLabels(strings), [strings]);
  const format = useCallback(
    (values: SourceValues) => formatClassicalWork(values, labels),
    [labels]
  );

  return (
    <SourceCitationBuilder
      example={strings.example}
      fields={fields}
      format={format}
      isReady={classicalWorkIsReady}
      strings={strings}
    />
  );
}
